import * as React from 'react';
import { Box, Divider, Grid, Paper, IconButton, Popover } from '@mui/material';
import SettingsIcon from '@mui/icons-material/MoreVert';
import { InvoiceDetailFields } from './InvoiceDetails';
import { ProductionInvoiceDetailFields } from './productionInvoiceDetails';
import {
	formatDate, formatNumber, toDate, formatDateBySetting, formatDateByIntelligence, normalizeUiFormat,
	convertFromMySQLFormatCustom, detectBillingPeriodFormat, isPeriodKey } from '../Common/Utilities';
import { VendorMapSelectControl, VendorMapSelectControl2, VendorMapSelectControl3 } from './VendorMapSelect';
import { ConstantsSymbols } from '../Common/Constants';
import { Settings } from '../InvoiceModels/InvoiceModelSetup/Settings';
import { MediaDetailsFields } from './MediaDetailsFields';
import { isNewUi } from '../../redesign/uiMode';
import { fieldCheck } from '../../redesign/vendormap/vendorMapLook';

export function MappedInvoiceFeilds(props) {

	const [dateFieldFormats, setDateFieldFormats] = React.useState({});
	const [anchorEl, setAnchorEl] = React.useState(null);
	const [selectedDateField, setSelectedDateField] = React.useState(null);
	const selectedInvoiceUserStatus = React.useMemo(() => {
		return (props.invoiceUserStatuses || []).find(
			(item) => String(item.invoiceUserStatusId) === String(props.invoiceUserStatusId || '')
		);
	}, [props.invoiceUserStatuses, props.invoiceUserStatusId]);

	const isInvalidDate = React.useCallback((value) => {
		return (new Date(value) == 'Invalid Date' || !props.regex['date'].some(e => e.test(value || '')))
	}, []);
	const tokens = React.useMemo(() => {
		return ['st', 'th', 'rd', 'nd', 'ST', 'TH','ND','RD','St','Th','Nd','Rd'];
	},[])
	const removeNotRequiredTokens = React.useCallback((value) => {
		if (!value) return value;
		for (let token of tokens) {
			value = value.replace(token, '');
		}
		return value;
	}, []);

	const handleHasTaxChange = (event) => {
		const isChecked = event.target.checked;
		props.hasTax_changeHandler(event);

		if (!isChecked) {

			props.tax_changeHandler({ event: "" });
			props.invoiceTax_changeHandler("");
		}
	};

	const broadcastTaxValues = (props.mappedFields?.invoiceTax?.value || '')
		.split(ConstantsSymbols.TAXKEYS_DELIM);
	const updateBroadcastTax = (index, value) => {
		const values = [...broadcastTaxValues];
		values[index] = value?.join(ConstantsSymbols.TAXKEYS_DELIM) || '';
		props.invoiceTax_changeHandler(values.filter(Boolean).join(ConstantsSymbols.TAXKEYS_DELIM));
	};
	const getBroadcastTaxAmount = (index) => {
		const selectedKeys = (broadcastTaxValues[index] || '').split(ConstantsSymbols.TAXKEYS_DELIM).filter(Boolean);
		const amount = selectedKeys.reduce((total, key) => {
			const field = props.fieldData?.KeyValues?.find(item => item.Key === key);
			return total + (parseFloat(String(field?.Value ?? '').replace(/[^0-9.-]/g, '')) || 0);
		}, 0);

		return selectedKeys.length ? amount.toFixed(2) : '';
	};

	const openDateFormatPopover = (field) => (event) => {
		setSelectedDateField(field);
		setAnchorEl(event.currentTarget);

		const isBillingPeriodField =
			(field === "serviceTermStartDate" || field === "serviceTermEndDate") &&
			isPeriodKey(props.mappedFields?.[field]?.mappedField);

		const savedFormat = props.dateFormats[getDateFormatKey(field)] || "";

		let defaultFormat =
			savedFormat ||
			formatDateByIntelligence(String(props.mappedFields[field]?.value ?? "")) ||
			"";

		if (isBillingPeriodField && !savedFormat) {
			const raw = String(
				props.mappedFields?.[field]?.rawValue ??
				props.mappedFields?.[field]?.value ??
				""
			);

			let fmt = detectBillingPeriodFormat(raw);
			if (typeof fmt === "string" && fmt.includes("%")) {
				fmt = convertFromMySQLFormatCustom(fmt);
			}
			defaultFormat = normalizeUiFormat(fmt) || "";
		}

		setDateFieldFormats((prev) => ({
			...prev,
			[field]: defaultFormat,
		}));
	};

	const closeDateFormatPopover = () => {
		setAnchorEl(null);
		setSelectedDateField(null);
	};

	const handleSettingsChange = (event, newSettings) => {
		const selectedFormat = newSettings.fieldFormat || formatDateByIntelligence(props.mappedFields[selectedDateField]?.value);

		setDateFieldFormats(prev => ({
			...prev,
			[selectedDateField]: selectedFormat
		}));

		props.fieldFormat_changeHandler(selectedDateField, selectedFormat);
	};

	const getCalculatedInvoiceAmount = () => {
		const netRaw = props?.mappedFields?.invoiceAmount?.value || '';
		const net = parseFloat(String(netRaw).replace(/[^\d.-]/g, '')) || 0;

		const taxRaw = props.hasInvoiceTax
			? props.invoiceTaxAmount?.calculatedAmount
			: props.taxAmount?.calculatedAmount;

		const tax = parseFloat(String(taxRaw).replace(/[^\d.-]/g, '')) || 0;

		const total = net + tax;

		return total.toLocaleString(undefined, {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		});
	};

	const isBillingPeriodField =
		(selectedDateField === "serviceTermStartDate" || selectedDateField === "serviceTermEndDate") &&
		isPeriodKey(props.mappedFields?.[selectedDateField]?.mappedField);


	const getDateFormatKey = (fieldName) => {
		switch (fieldName) {
			case 'invoiceDate':
				return 'invoiceDateFieldFormat';
			case 'dueDate':
				return 'dueDateFieldFormat';
			case 'serviceTermStartDate':
				return 'serviceTermStartDateFieldFormat';
			case 'serviceTermEndDate':
				return 'serviceTermEndDateFieldFormat';
			default:
				return '';
		}
	};


	const fields = React.useMemo(() => {
		let fields = [{
			label: 'Invoice Number',
			field: 'invoiceNumber',
			type: 'string',
			disbaled:props.readOnly,
			visible:true
		},
			{
				label: 'Invoice Date',
				field: 'invoiceDate',
				type: 'date',
				transformer: ((fieldName) => ({ value, type }) => {
					value = removeNotRequiredTokens(value);

					//if (props?.mappedFields?.[fieldName]?.type !== 'date') {
						try {
							// Ensure the correct date format is applied
							return isInvalidDate(value) ? value : toDate(new Date(value));
							//const fieldFormat = props.dateFormats?.[`${fieldName}FieldFormat`] || "";
							//return formatDateBySetting(value, fieldFormat);
						} catch (ex) {
							return value;
						}
					//}

					try {
						return isInvalidDate(value) ? value : toDate(new Date(value));
					} catch (ex) {
						return value;
					}
				})('invoiceDate'),
				disbaled: props.readOnly,
				visible: true
			}, {
				label: 'Due Date',
				field: 'dueDate',
				type: 'date',
				disbaled: props.readOnly,
				transformer: ((fieldName) => ({ value, type }) => {
					value = removeNotRequiredTokens(value);

					//if (props?.mappedFields?.[fieldName]?.type !== 'date') {
					try {
						// Ensure the correct date format is applied
						return isInvalidDate(value) ? value : toDate(new Date(value));
						//const fieldFormat = props.dateFormats?.[`${fieldName}FieldFormat`] || "";
						//return formatDateBySetting(value, fieldFormat);
					} catch (ex) {
						return value;
					}
					//}

					try {
						return isInvalidDate(value) ? value : toDate(new Date(value));
					} catch (ex) {
						return value;
					}
				})('dueDate'),
				visible: true
			},
			{
				label: props.modelDefId === 3 ? 'Start Date' : 'Service Start Date',
				field: 'serviceTermStartDate',
				type: 'date',
				disbaled: props.readOnly,
				transformer: ((fieldName) => ({ value, type }) => {
					value = removeNotRequiredTokens(value);

					//if (props?.mappedFields?.[fieldName]?.type !== 'date') {
					try {
						// Ensure the correct date format is applied
						//const fieldFormat = props.dateFormats?.[`${fieldName}FieldFormat`] || "";
						//return formatDateBySetting(value, fieldFormat);
						return isInvalidDate(value) ? value : toDate(new Date(value));
					} catch (ex) {
						return value;
							}
					//}

					try {
						return isInvalidDate(value) ? value : toDate(new Date(value));
					} catch (ex) {
						return value;
					}
				})('serviceTermStartDate'),
				visible: props.modelDefId === 1 || props.modelDefId === 3
			},
			{
				label: props.modelDefId === 3 ? 'End Date' : 'Service End Date',
				field: 'serviceTermEndDate',
				type: 'date',
				disbaled: props.readOnly,
				transformer: ((fieldName) => ({ value, type }) => {
					value = removeNotRequiredTokens(value);

					//if (props?.mappedFields?.[fieldName]?.type !== 'date') {
					try {
						// Ensure the correct date format is applied
						//const fieldFormat = props.dateFormats?.[`${fieldName}FieldFormat`] || "";
						//return formatDateBySetting(value, fieldFormat);
						return isInvalidDate(value) ? value : toDate(new Date(value));
					} catch (ex) {
						return value;
							}
					//}

					try {
						return isInvalidDate(value) ? value : toDate(new Date(value));
					} catch (ex) {
						return value;
					}
				})('serviceTermEndDate'),
				visible: props.modelDefId === 1 || props.modelDefId === 3
			},
		{
			label: 'Payment Term',
			type: 'string',
			field: 'paymentTermCode',
			disbaled: props.readOnly,
			visible: true
		}, {
			label: 'Vendor Currency',
			type: 'string',
			field: 'invoiceCurrency',
			disbaled: props.readOnly,
			visible:true
			}, {
			label: ((props.hasTax)) ? 'Net Amount' : 'Invoice Amount',
				type: 'currency',
				field: 'invoiceAmount',
				disbaled: props.readOnly,
				visible: true,
					transformer: ({ value }) => {
    if (!value) return '';
	const sanitized = String(value).replace(/[a-zA-Z\$€£\s]/g, '');
	const normalized = sanitized.replace(/,/g, '');
	const number = parseFloat(normalized);
    // Return the 'sanitized' version even if it fails parseFloat, instead of raw 'value'
	return isNaN(number) ? sanitized : formatNumber(number);
}
				},
			{
				label: 'Total spots',
				type: 'currency',
				field: 'totalSpots',
				disbaled: props.readOnly,
				visible: props.modelDefId === 3,
				filterCallback: (records) => {
					return records.filter((e, i) => {

						let nValue = (e?.Value + '').replaceAll(",", '');
						return /^\d+$/.test(nValue);
					})
				}
			},
			{
				label: 'Client',
				type: 'string',
				field: 'broadcastClient',
				disbaled: props.readOnly,
				visible: props.modelDefId === 3
			},
			{
				label: 'Product',
				type: 'string',
				field: 'broadcastProduct',
				disbaled: props.readOnly,
				visible: props.modelDefId === 3
			},
			{
				label: 'Project/Job',
				type: 'string',
				field: 'level2Key',
				disbaled: props.readOnly,
				visible: props.modelDefId === 1
			},
		];
		return fields;
	}, [dateFieldFormats, props.dateFormats, props.hasTax]);

	// The value a field shows, and whether it passes the field's check (the red border in Classic).
	const displayOf = (item) => {
		const displayValue = item.field === 'invoiceCurrency'
			? (props.vendor && props.vendor.vendorCode ? props.invoiceCurrency : '')
			: item.transformer
				? item.transformer(props?.mappedFields[item.field])
				: props?.mappedFields[item.field]?.value || '';
		const passesRegex = (props.regex[item.type] || [])?.some(e => e.test(displayValue));
		const isValid = passesRegex
			|| (item.field !== 'paymentTermCode' && item.type === 'string')
			|| (item.field === 'paymentTermCode' && props.paymentTerms && props.paymentTerms[props?.mappedFields[item.field]?.value?.toLowerCase()])
			|| displayValue === '';
		return { displayValue, passesRegex, isValid };
	};
	const hasDateFormatMenu = (item) => props.advanceEdit &&
		item.type === 'date' &&
		props?.mappedFields[item.field]?.value && (
			(props?.mappedFields[item.field]?.type !== 'date') || (isPeriodKey(props?.mappedFields[item.field]?.mappedField || ''))
		);

	// New UI (docs/ui_redesign/16-vendor-mapping-and-dashboard-concepts.md, "Vendor Mapping", option B): one table of
	// invoice field, where it is read from, the value on this invoice, its format and a check. The same controls,
	// handlers and checks as the Classic rows below; only the layout differs.
	if (isNewUi()) {
		const byField = Object.fromEntries(fields.map(f => [f.field, f]));
		const keyCell = (value, select, fallback) => props.advanceEdit ? select : <span className="apw2-vm-key">{value || fallback || '—'}</span>;
		const check = c => <span className={'apw2-vm-check ' + c.tone}>{c.tone === 'ok' ? '✓ ' : ''}{c.text}</span>;
		let found = 0, workedOut = 0;
		const fieldRow = (item, index) => {
			if (!item || !item.visible) return null;
			const v = displayOf(item);
			const mapped = props?.mappedFields[item.field] || {};
			const c = fieldCheck(item.field, v, mapped.mappedField);
			if (c.tone === 'ok') found++; else if (c.tone === 'calc') workedOut++;
			const format = item.type === 'date' ? (props.dateFormats[getDateFormatKey(item.field)] || '') : '';
			return (
				<tr key={item.field}>
					<th scope="row">{item.label}</th>
					<td>{keyCell(mapped.mappedField, <VendorMapSelectControl
						style={props.style}
						fieldData={
							(item.field === 'serviceTermStartDate' || item.field === 'serviceTermEndDate')
								? { ...props.fieldData, Records: [...(props.fieldData?.Records || []), ...(props.fieldData?.BillingPeriodOption || [])] }
								: props.fieldData
						}
						value={props?.mappedFields[item.field]}
						recordType={'Records'}
						type={item.type}
						fieldInfo={item}
						readOnly={item.disbaled}
						key={'selectMapped' + index}
						controlKey={index}
						regex={props.regex}
						onChange={(event) => { props.field_changehandler({ event: event, value: item.field }) }} />,
						item.field === 'invoiceCurrency' ? 'The vendor' : item.field === 'dueDate' && v.displayValue ? 'Payment term' : '')}</td>
					<td><input type="text" title={mapped.title || ''} value={v.displayValue} readOnly aria-label={item.label}
						className={'apw2-vm-val' + (v.isValid ? '' : ' border-red')} /></td>
					<td className="apw2-vm-fmt">
						{format ? <span className="apw2-vm-key">{format}</span> : null}
						{hasDateFormatMenu(item) && <IconButton size="small" aria-label={`Date format for ${item.label}`} title="Date format" onClick={openDateFormatPopover(item.field)}><SettingsIcon fontSize="small" /></IconButton>}
					</td>
					<td>{check(c)}</td>
				</tr>
			);
		};
		const simpleRow = (label, field, controlKey, extra = {}) => {
			const value = props?.mappedFields[field]?.value;
			if (value) found++;
			return (
				<tr key={field + (extra.suffix || '')}>
					<th scope="row">{label}</th>
					<td>{keyCell(props?.mappedFields[field]?.mappedField, <VendorMapSelectControl
						maxWeightedLine={extra.weighted ? props.maxWeightedLine : undefined}
						style={props.style} fieldData={props?.fieldData}
						value={props?.mappedFields[field]}
						type={'string'}
						readOnly={props.readOnly}
						controlKey={controlKey}
						onChange={(event) => { props.field_changehandler({ event: event, value: field }) }} />)}</td>
					<td><input type="text" value={value || ''} readOnly aria-label={label} className="apw2-vm-val" /></td>
					<td className="apw2-vm-fmt" />
					<td>{check(value ? { tone: 'ok', text: 'Found' } : { tone: 'mut', text: 'Not found' })}</td>
				</tr>
			);
		};
		const group = (title, extra) => <tr className="apw2-vm-grp" key={'g-' + title}><th colSpan={5} scope="colgroup"><span>{title}</span>{extra}</th></tr>;
		const wide = (key, node) => <tr className="apw2-vm-wide" key={key}><td colSpan={5}>{node}</td></tr>;

		const invoiceRows = ['invoiceNumber', 'invoiceDate', 'dueDate', 'serviceTermStartDate', 'serviceTermEndDate', 'paymentTermCode'].map((f, i) => fieldRow(byField[f], i));
		const codingRows = [
			fieldRow(byField.totalSpots, 20), fieldRow(byField.broadcastClient, 21), fieldRow(byField.broadcastProduct, 22), fieldRow(byField.level2Key, 23),
			((props.editorType === 'summary' || props.modelDefId === 2) && props.modelDefId !== 3) ? simpleRow(props.modelDefId === 1 ? 'IO/PO Number' : 'PO Code', 'ioNumber', 'b' + 1001, { weighted: true }) : null,
			(props.editorType === 'summary' && props.modelDefId === 2) ? simpleRow('Project/Job', 'level2Key', 'b' + 2002, { weighted: true, suffix: 'p' }) : null,
			(props.editorType === 'summary' && props.modelDefId === 3) ? wide('media', <MediaDetailsFields
				style={props.style}
				advanceEdit={props.advanceEdit}
				mappedFields={props.mappedFields}
				field_changehandler={props.field_changehandler}
				modelDefId={props.modelDefId}
				companyCode={props.documentVendorMapping.companyCode}
				invoiceDocumentId={props.info?.invoiceDocument?.invoiceDocumentId}
				vendorCode={props.vendor?.vendorCode}
				readOnly={props.readOnly || props.info.mode === 'View'}
				fieldData={props.fieldData}
				onBandValidationChange={props.onBandValidationChange}
				registerCallLetterValidator={props.registerCallLetterValidator}
				skipCallLetterBlurRef={props.skipCallLetterBlurRef}
			/>) : null,
			props.modelDefId === 3 ? simpleRow('Contract Number', 'contractNumber', 'contractNumberMapped') : null
		].filter(Boolean);
		const taxSwitches = (
			<span className="apw2-vm-grp-sw">
				<label><input type="checkbox" role="switch" checked={props.hasTax} onChange={handleHasTaxChange} disabled={props.readOnly} />Invoice has tax</label>
				{props.hasTax && <label><input type="checkbox" role="switch" checked={props.hasInvoiceTax} onChange={(event) => { props.hasInvoiceTax_changeHandler(event) }} disabled={props.readOnly} />Tax calculation based on invoice</label>}
			</span>
		);
		const amountRows = [fieldRow(byField.invoiceCurrency, 30), fieldRow(byField.invoiceAmount, 31)];
		if (!props.hasInvoiceTax && props.hasTax) {
			amountRows.push(
				<tr key="tax">
					<th scope="row">Tax</th>
					<td><VendorMapSelectControl2 style={props.style} fieldData={props?.taxddl} value={props.taxCode} type={'taxddl'} readOnly={props.readOnly} controlKey={'a' + 1001}
						onChange={(event) => { props.tax_changeHandler({ event: event.target.value }) }} /></td>
					<td><input type="text" value={props.taxAmount?.calculatedAmount} readOnly aria-label="Tax" className={'apw2-vm-val' + (props.taxAmount.isValid ? '' : ' border-red')} /></td>
					<td className="apw2-vm-fmt" />
					<td>{props.taxAmount?.calculatedAmount ? check(props.taxAmount.isValid ? { tone: 'ok', text: 'Matches the scan' } : { tone: 'warn', text: 'Differs from the scan' }) : check({ tone: 'mut', text: 'Choose a tax' })}</td>
				</tr>
			);
		}
		if (props.hasInvoiceTax && props.hasTax && props.modelDefId === 3) {
			['State Tax', 'Local Tax'].forEach((label, index) => amountRows.push(
				<tr key={label}>
					<th scope="row">{label}</th>
					<td><VendorMapSelectControl3 style={props.style} fieldData={props?.fieldData} value={broadcastTaxValues[index] || ''} type={'invoiceTaxKeysddl'} readOnly={props.readOnly} controlKey={'broadcastTax' + index} regex={props.regex}
						onChange={(event) => updateBroadcastTax(index, event.target.value)} /></td>
					<td><input type="text" value={getBroadcastTaxAmount(index)} readOnly aria-label={label} className="apw2-vm-val" /></td>
					<td className="apw2-vm-fmt" />
					<td>{check(getBroadcastTaxAmount(index) ? { tone: 'ok', text: 'Found' } : { tone: 'mut', text: 'Not chosen' })}</td>
				</tr>
			));
		} else if (props.hasInvoiceTax && props.hasTax) {
			amountRows.push(
				<tr key="invoiceTax">
					<th scope="row">Invoice Tax</th>
					<td><VendorMapSelectControl3 style={props.style} fieldData={props?.fieldData} value={props.mappedFields['invoiceTax'].value} type={'invoiceTaxKeysddl'} readOnly={props.readOnly} controlKey={'a' + 10043} regex={props.regex}
						onChange={(event) => { const value = event.target.value; props.invoiceTax_changeHandler(value?.join(ConstantsSymbols.TAXKEYS_DELIM)); }} /></td>
					<td><input type="text" value={props.invoiceTaxAmount?.calculatedAmount} readOnly aria-label="Invoice tax" className={'apw2-vm-val' + (props.invoiceTaxAmount.isValid ? '' : ' border-red')} /></td>
					<td className="apw2-vm-fmt" />
					<td>{props.invoiceTaxAmount?.calculatedAmount ? check(props.invoiceTaxAmount.isValid ? { tone: 'ok', text: 'Matches the scan' } : { tone: 'warn', text: 'Differs from the scan' }) : check({ tone: 'mut', text: 'Not chosen' })}</td>
				</tr>
			);
		}
		if (props.hasTax) {
			workedOut++;
			amountRows.push(
				<tr key="total" className="apw2-vm-total">
					<th scope="row">Invoice Amount</th>
					<td><span className="apw2-vm-key plain">Net + tax</span></td>
					<td><input type="text" readOnly value={getCalculatedInvoiceAmount()} aria-label="Invoice amount" className="apw2-vm-val" /></td>
					<td className="apw2-vm-fmt" />
					<td>{check({ tone: 'calc', text: 'Worked out' })}</td>
				</tr>
			);
		}
		const tableBody = [
			group('Invoice'), ...invoiceRows,
			codingRows.length ? group('Coding') : null, ...codingRows,
			group('Amount and tax', taxSwitches), ...amountRows,
			group('Workflow'),
			<tr key="userStatus">
				<th scope="row">{props.userStatusDisplayName || 'User Status'}</th>
				<td colSpan={2}>
					<select name="invoiceUserStatusId" value={props.invoiceUserStatusId || ''} disabled={props.readOnly} onChange={props.invoiceUserStatus_changeHandler} aria-label={props.userStatusDisplayName || 'User Status'}>
						<option value="">Select {props.userStatusDisplayName || 'User Status'}</option>
						{(props.invoiceUserStatuses || []).map((item, index) => (
							<option key={item.invoiceUserStatusId || index} value={item.invoiceUserStatusId}>{item.userStatusCode}</option>
						))}
					</select>
				</td>
				<td className="apw2-vm-fmt" />
				<td />
			</tr>,
			props.editorType === 'detail' ? group('Invoice details') : null,
			props.editorType === 'detail' ? wide('details', props.info.modelDefId === 1
				? <InvoiceDetailFields field_changehandler={props.field_changehandler} fieldData={props?.fieldData} style={props.style} mappedFields={props?.mappedFields} maxWeightedLine={props?.maxWeightedLine} advanceEdit={props.advanceEdit} readOnly={props.readOnly} regex={props.regex} />
				: <ProductionInvoiceDetailFields style={props.style} field_changehandler={props.field_changehandler} fieldData={props?.fieldData} mappedFields={props?.mappedFields} maxWeightedLine={props?.maxWeightedLine} advanceEdit={props.advanceEdit} readOnly={props.readOnly} regex={props.regex} />) : null
		].filter(Boolean);

		return (<>
			<section className="apw2-vm-card" aria-labelledby="apw2-vm-map-h">
				<div className="apw2-vm-card-h">
					<h2 id="apw2-vm-map-h">Field mapping</h2>
					<span className="apw2-vm-sub">{found} read from this invoice{workedOut ? ` · ${workedOut} worked out` : ''}. Focus a value to see where it is on the document.</span>
					<span className="apw2-grow" />
					{props.advanceEdit_changeHandler && (
						<label className="apw2-vm-adv" htmlFor="chkAdvanceEdit">
							<input id={'chkAdvanceEdit'} type="checkbox" role="switch" onChange={props.advanceEdit_changeHandler} checked={props.advanceEdit} />Advance edit
						</label>
					)}
				</div>
				<div className="apw2-vm-table-wrap">
					<table className={'apw2-vm-table' + (props.advanceEdit ? ' adv' : '')}>
						<colgroup><col className="c1" /><col className="c2" /><col className="c3" /><col className="c4" /><col className="c5" /></colgroup>
						<thead><tr><th scope="col">Invoice field</th><th scope="col">Read from</th><th scope="col">Value on this invoice</th><th scope="col">Format</th><th scope="col">Check</th></tr></thead>
						<tbody>{tableBody}</tbody>
					</table>
				</div>
			</section>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={closeDateFormatPopover}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Settings
					settings={{
						fieldFormat: dateFieldFormats[selectedDateField] ||
							formatDateByIntelligence(props.mappedFields[selectedDateField]?.value) ||
							"MM d yyyy"
					}}
					info={props.info}
					name={selectedDateField || ''}
					readOnly={props.readOnly}
					onChange={handleSettingsChange}
					showAlignment={false}
					showStringFormat={false}
					showDateFormat={!isBillingPeriodField}
					showDateRange={false}
					showCurrencyFormat={false}
					onDefaultValueClick={props.onDefaultValueClick}
					isBillingPeriod={isBillingPeriodField}
					isModelMapping={false}
					openMenuAnchor={anchorEl}
					closeMenu={closeDateFormatPopover}
					customFormats={isBillingPeriodField ? 'BillingPeriodFormats' : null}
					inlineExamples
				/>
			</Popover>
		</>);
	}

	return (<><div className="mf-12">
                <h2>Mapped Invoice Fields</h2>
		<Paper className='mappedFieldsWrapper'>
                    <Box>
				<Grid container key={100} spacing={props.stye?.mainRowSpacing} className='mappedFieldsHeader'>
					<Grid item xs={props.style.cellOneWidthl} sx={props.style.cellOneStyle}>{!props.advanceEdit && <label style={{ padding:'5px 0 5px 0' }}>System Label</label>}</Grid>
					{props.advanceEdit && <Grid key={'a' + 100} item xs={props.style.cellTwoWidth} >{props.advanceEdit && <label style={{ padding: '5px 0 5px 0' }}>Label</label>}</Grid>}
					<Grid item key={'b' + 100} xs={props.style.cellThreeWidth} ><label style={{ padding: '5px 0 5px 0' }}>Sample Value From Invoice</label></Grid>
                            </Grid>
                        {fields.map((item, index) => {
						return (item.visible) ? (<Grid container key={index} spacing={props.style.mainRowSpacing} sx={props.style.mainRowStyle}>
						<Grid item key={'MappedFielditem' + index} xs={props.style.cellOneWidth} sx={props.style.cellOneStyle}><label key={'MappedFielditem1' + index}>{item.label}</label></Grid>

						{
							< Grid item key={'a' + index} xs={props.style.cellTwoWidth}
								style={{ display: props.advanceEdit ? 'block' : 'none' }}
								sx={props.style.cellTwoStyle}>
									{<VendorMapSelectControl
										style={props.style}
										fieldData={
											(item.field === 'serviceTermStartDate' || item.field === 'serviceTermEndDate')
												? {
													...props.fieldData,
													Records: [
														...(props.fieldData?.Records || []),
														...(props.fieldData?.BillingPeriodOption || [])
													]
												}
												: props.fieldData
										}
										value={props?.mappedFields[item.field]}
										recordType={'Records'}
										type={item.type}
										fieldInfo={item }
									readOnly={/*props.readOnly ||*/ item.disbaled}
									key={'selectMapped'+index}
										controlKey={index}
									regex={props.regex }
										onChange={(event) => {
										props.field_changehandler({ event: event, value: item.field })
										}} ></VendorMapSelectControl>}
                                    </Grid>
						}

							{<Grid item key={'b' + index} xs={props.style.cellThreeWidth} sx={props.style.cellThreeStyle}>
								<div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
									{(() => {
										// 1. Calculate the final sanitized/formatted value once
										const displayValue = item.field === 'invoiceCurrency'
											? (props.vendor && props.vendor.vendorCode ? props.invoiceCurrency : '')
											: item.transformer
												? item.transformer(props?.mappedFields[item.field])
												: props?.mappedFields[item.field]?.value || '';

										// 2. Test the regex against the SANITIZED displayValue, not the raw value
										const passesRegex = (props.regex[item.type] || [])?.some(e => e.test(displayValue));

										const isValid = passesRegex
											|| (item.field !== 'paymentTermCode' && item.type === 'string')
											|| (item.field === 'paymentTermCode' && props.paymentTerms && props.paymentTerms[props?.mappedFields[item.field]?.value?.toLowerCase()])
											|| displayValue === '';

										return (
											<input
												type="text"
												title={props?.mappedFields[item.field].title || ''}
												value={displayValue}
												readOnly
												className={`${isValid ? '' : 'border-red'}`}
												style={{
													...props.style.inputStyle,
													width: '100%',
													paddingRight: props.advanceEdit && item.type === 'date' ? '40px' : undefined,
													textAlign: item.type.indexOf('currency') !== -1 && passesRegex ? 'right' : 'left',
												}}
											/>
										);
									})()}

									{props.advanceEdit &&
										item.type === 'date' &&
										props?.mappedFields[item.field]?.value && (
											(
												props?.mappedFields[item.field]?.type !== 'date'
											) || (
												isPeriodKey(props?.mappedFields[item.field]?.mappedField || '')
											)
										) && (
											<IconButton
												onClick={openDateFormatPopover(item.field)}
												style={{
													position: 'absolute',
													right: 0,
													top: '50%',
													transform: 'translateY(-50%)',
													padding: 0,
												}}
											>
												<SettingsIcon />
											</IconButton>
										)}
								</div>


							</Grid>

						}
					</Grid>)
						: <React.Fragment key={ 'fragMapped'+index}></React.Fragment>
                        })}



				{((props.editorType === 'summary' || props.modelDefId === 2) && props.modelDefId !== 3) && <Grid container spacing={props.style.mainRowSpacing} sx={props.style.mainRowStyle}>
					<Grid item xs={props.style.cellOneWidth} sx={props.style.cellOneStyle}><label>{ props.modelDefId===1 ? 'IO/PO Number': 'PO Code' }</label></Grid>
					{
						<Grid item xs={props.style.cellTwoWidth} style={{ display: props.advanceEdit ? 'block':'none' }} sx={props.style.cellTwoStyle}>
                                    <VendorMapSelectControl
                                        maxWeightedLine={props.maxWeightedLine}
							style={props.style} fieldData={props?.fieldData}
							value={props?.mappedFields['ioNumber']}
								type={'string'}
                                        readOnly={props.readOnly}
							controlKey={'b' + 1001 }
                                        onChange={(event) => {
								props.field_changehandler({ event: event, value: 'ioNumber' })
                                        }}
                                >
						</VendorMapSelectControl>
                                </Grid>
					}
					<Grid item key={'b' + 101} xs={props.style.cellThreeWidth} sx={props.style.cellThreeStyle}><input type={'text'}
						value={props?.mappedFields['ioNumber']?.value}
						readOnly
						style={{ ...props.style.inputStyle }} /></Grid>
				</Grid>}

				{(props.editorType === 'summary' && props.modelDefId === 2) && (
					<Grid container spacing={props.style.mainRowSpacing} sx={props.style.mainRowStyle}>
						<Grid item xs={props.style.cellOneWidth} sx={props.style.cellOneStyle}>
							<label>Project/Job</label>
						</Grid>
						<Grid item xs={props.style.cellTwoWidth} style={{ display: props.advanceEdit ? 'block' : 'none' }} sx={props.style.cellTwoStyle}>
							<VendorMapSelectControl
								maxWeightedLine={props.maxWeightedLine}
								style={props.style}
								fieldData={props?.fieldData} // Ensure this is consistent with detail
								value={props?.mappedFields['level2Key']}
								type={'string'}
								readOnly={props.readOnly}
								controlKey={'b' + 2002} // Ensure unique controlKey
								onChange={(event) => {
									props.field_changehandler({ event: event, value: 'level2Key' });
								}}
							/>
						</Grid>
						<Grid item key={'b' + 202} xs={props.style.cellThreeWidth} sx={props.style.cellThreeStyle}>
							<input
								type={'text'}
								value={props?.mappedFields['level2Key']?.value}
								readOnly
								style={{ ...props.style.inputStyle }}
							/>
						</Grid>
					</Grid>
				)}

				{props.editorType === 'summary'
					&& props.modelDefId === 3
					&& (
					<MediaDetailsFields
							style={props.style}
							advanceEdit={props.advanceEdit}
							mappedFields={props.mappedFields}
							field_changehandler={props.field_changehandler}
							modelDefId={props.modelDefId}
							companyCode={props.documentVendorMapping.companyCode}
							invoiceDocumentId={props.info?.invoiceDocument?.invoiceDocumentId}
							vendorCode={props.vendor?.vendorCode}
							readOnly={props.readOnly || props.info.mode === 'View'}
							fieldData={props.fieldData}
							onBandValidationChange={props.onBandValidationChange}
							registerCallLetterValidator={props.registerCallLetterValidator}
							skipCallLetterBlurRef={props.skipCallLetterBlurRef}
						/>
				)}

					<Grid container spacing={props.style.mainRowSpacing} sx={props.style.mainRowStyle}>
						<Grid item xs={props.style.cellOneWidth} sx={props.style.cellOneStyle}>
							<label>{props.userStatusDisplayName || 'User Status'}</label>
						</Grid>
						<Grid item xs={props.style.cellTwoWidth} sx={props.style.cellTwoStyle}>
							<select
								name="invoiceUserStatusId"
								value={props.invoiceUserStatusId || ''}
								disabled={props.readOnly}
								onChange={props.invoiceUserStatus_changeHandler}
								style={{ ...props.style.inputStyle, width: '100%', padding: '5px 10px' }}
							>
								<option value="">Select {props.userStatusDisplayName || 'User Status'}</option>
								{(props.invoiceUserStatuses || []).map((item, index) => (
									<option key={item.invoiceUserStatusId || index} value={item.invoiceUserStatusId}>
										{item.userStatusCode}
									</option>
								))}
							</select>
						</Grid>
					</Grid>

					{props.modelDefId === 3 && (
						<Grid container spacing={props.style.mainRowSpacing} sx={props.style.mainRowStyle}>
							<Grid item xs={props.style.cellOneWidth} sx={props.style.cellOneStyle}><label>Contract Number</label></Grid>
							<Grid item xs={props.style.cellTwoWidth} style={{ display: props.advanceEdit ? 'block' : 'none' }} sx={props.style.cellTwoStyle}>
								<VendorMapSelectControl
									style={props.style}
									fieldData={props?.fieldData}
									value={props?.mappedFields['contractNumber']}
									type={'string'}
									readOnly={props.readOnly}
									controlKey={'contractNumberMapped'}
									onChange={(event) => {
										props.field_changehandler({ event: event, value: 'contractNumber' })
									}}
								>
								</VendorMapSelectControl>
							</Grid>
							<Grid item xs={props.style.cellThreeWidth} sx={props.style.cellThreeStyle}><input type={'text'}
								value={props?.mappedFields['contractNumber']?.value}
								readOnly
								style={{ ...props.style.inputStyle }} /></Grid>
						</Grid>
					)}



				{
					<>
				<Grid container key={10000} spacing={props.style.mainRowSpacing} sx={props.style.mainRowStyle}>
						<Grid item xs={props.style.cellOneWidth} sx={props.style.cellOneStyle}><label>{'Invoice Has Tax'}</label></Grid>
						<Grid key={'a10001'} item container alignItems={'center'}
							xs={props.style.cellTwoWidth} sx={props.style.cellTwoStyle}>
							<input style={{ marginTop: '5px' }} checked={props.hasTax}
								onChange={handleHasTaxChange}
								type='checkbox' disabled={props.readOnly} />

                        </Grid>
					</Grid>
						{props.hasTax && <Grid container key={10035} spacing={props.style.mainRowSpacing} sx={props.style.mainRowStyle}>
							<Grid
								item
								xs={props.style.cellOneWidth}
								sx={{
									...props.style.cellOneStyle,
									display: 'flex',
									alignItems: 'center',
									flexWrap: 'wrap',
									minWidth: 0
								}}
							>
								<label
									style={{
										display: 'block',
										maxWidth: '100%',
										whiteSpace: 'normal',
										wordBreak: 'break-word',
										lineHeight: 1.2,
										overflowWrap: 'break-word',
										flex: '1 1 0',
										minWidth: 0
									}}
								>
									Tax Calculation based on invoice
								</label>
							</Grid>
							<Grid
								key={'a10001'}
								item
								container
								alignItems={'center'}
								xs={props.style.cellTwoWidth}
								sx={props.style.cellTwoStyle}
								style={{
									display: 'flex',
									alignItems: 'center',
									minWidth: 0
								}}
							>
								<input
									style={{ marginTop: '5px', flexShrink: 0 }}
									checked={props.hasInvoiceTax}
									onChange={(event) => { props.hasInvoiceTax_changeHandler(event) }}
									type='checkbox'
									disabled={props.readOnly}
								/>
							</Grid>
						</Grid>
						}
					</>
				}

				{!props.hasInvoiceTax && props.hasTax /*&& (props.taxSettings === 1 || props.taxSettings === "" || props.taxSettings === null)*/ ? (<Grid container key={101} spacing={props.style.mainRowSpacing} sx={props.style.mainRowStyle}>
					<Grid item xs={props.style.cellOneWidth} sx={props.style.cellOneStyle}><label>Tax</label></Grid>
					< Grid item key={'a' + 102} xs={props.style.cellTwoWidth} sx={props.style.cellTwoStyle}>
                                    <VendorMapSelectControl2
							style={props.style} fieldData={props?.taxddl}
                                        value={props.taxCode}
							type={'taxddl'}
                                        readOnly={props.readOnly}
							controlKey={'a' + 1001}
                                        onChange={(event) => {
								props.tax_changeHandler({ event: event.target.value })
                                        }}
                                >
						</VendorMapSelectControl2>
						</Grid>
					<Grid item key={'b' + 103} xs={props.style.cellThreeWidth} sx={props.style.cellThreeStyle}><input type={'text'}
                                        value={props.taxAmount?.calculatedAmount}
						className={`${props.taxAmount.isValid ? '' : 'border-red'}`}
								readOnly
						style={{ ...props.style.inputStyle, textAlign: 'right' }} /></Grid>
				</Grid>) : <></>}

				{props.hasInvoiceTax && props.hasTax && props.modelDefId === 3 ? ['State Tax', 'Local Tax'].map((label, index) => (
					<Grid container key={label} spacing={props.style.mainRowSpacing} sx={props.style.mainRowStyle}>
						<Grid item xs={props.style.cellOneWidth} sx={props.style.cellOneStyle}><label>{label}</label></Grid>
						<Grid item key={'broadcastTax' + index} xs={props.style.cellTwoWidth} sx={props.style.cellTwoStyle}>
							<VendorMapSelectControl3
								style={props.style} fieldData={props?.fieldData}
								value={broadcastTaxValues[index] || ''}
								type={'invoiceTaxKeysddl'}
								readOnly={props.readOnly}
								controlKey={'broadcastTax' + index}
								regex={props.regex}
								onChange={(event) => updateBroadcastTax(index, event.target.value)}
							>
							</VendorMapSelectControl3>
						</Grid>
						<Grid item key={'broadcastTaxAmount' + index} xs={props.style.cellThreeWidth} sx={props.style.cellThreeStyle}><input type={'text'}
							value={getBroadcastTaxAmount(index)}
							readOnly
							style={{ ...props.style.inputStyle, textAlign: 'right' }} /></Grid>
					</Grid>
				)) : props.hasInvoiceTax && props.hasTax ? (<Grid container key={1011} spacing={props.style.mainRowSpacing} sx={props.style.mainRowStyle}>
					<Grid item xs={props.style.cellOneWidth} sx={props.style.cellOneStyle}><label>Invoice Tax</label></Grid>
					<Grid item key={'a' + 1031} xs={props.style.cellTwoWidth} sx={props.style.cellTwoStyle}>
						<VendorMapSelectControl3
							style={props.style} fieldData={props?.fieldData}
							value={props.mappedFields['invoiceTax'].value}
							type={'invoiceTaxKeysddl'}
							readOnly={props.readOnly}
							controlKey={'a' + 10043}
							regex={props.regex}
							onChange={(event) => {
								let value = event.target.value;
								props.invoiceTax_changeHandler(value?.join(ConstantsSymbols.TAXKEYS_DELIM));
							}}
						>
						</VendorMapSelectControl3>
					</Grid>
					<Grid item key={'b' + 1021} xs={props.style.cellThreeWidth} sx={props.style.cellThreeStyle}><input type={'text'}
						value={props.invoiceTaxAmount?.calculatedAmount}
						className={`${props.invoiceTaxAmount.isValid ? '' : 'border-red'}`}
						readOnly
						style={{ ...props.style.inputStyle, textAlign: 'right' }} /></Grid>
				</Grid>) : <></>}

				{(props.hasTax) ? (
					<Grid container spacing={props.style.mainRowSpacing} sx={props.style.mainRowStyle}>
						<Grid item xs={props.style.cellOneWidth} sx={props.style.cellOneStyle}>
							<label>Invoice Amount</label>
						</Grid>
						{/*<Grid item xs={props.style.cellTwoWidth} sx={props.style.cellTwoStyle}></Grid>*/}
						<Grid item xs={props.style.cellTwoWidth} sx={props.style.cellTwoStyle}>
							<input
								type="text"
								readOnly
								value={getCalculatedInvoiceAmount()}
								style={{ ...props.style.inputStyle, textAlign: 'right' }}
							/>
						</Grid>
					</Grid>
				) : <></>}



				{props.editorType === 'detail' && (<><h2 className='subHeader'>Invoice Details</h2>
					<Divider key={ 'mappedFieldDivider'} className='subHeaderUnderLine' orientation="vertical" variant="middle" flexItem />
					{props.info.modelDefId === 1 ? <InvoiceDetailFields
						field_changehandler={props.field_changehandler}
						fieldData={props?.fieldData}
						style={props.style}
						mappedFields={props?.mappedFields}
						maxWeightedLine={props?.maxWeightedLine}
						advanceEdit={props.advanceEdit}
						readOnly={props.readOnly}
						regex={props.regex}
						></InvoiceDetailFields>
							: <ProductionInvoiceDetailFields style={props.style}
								field_changehandler={props.field_changehandler}
								fieldData={props?.fieldData}
							mappedFields={props?.mappedFields}
							maxWeightedLine={props?.maxWeightedLine}
							advanceEdit={props.advanceEdit}
							readOnly={props.readOnly}
							regex={props.regex}
							></ProductionInvoiceDetailFields>
					}</>)
				}
			</Box>
		</Paper>

	</div>
		<Popover
			open={Boolean(anchorEl)}
			anchorEl={anchorEl}
			onClose={closeDateFormatPopover}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			transformOrigin={{ vertical: 'top', horizontal: 'right' }}
		>
			
			<Settings
				settings={{
					fieldFormat: dateFieldFormats[selectedDateField] ||
						formatDateByIntelligence(props.mappedFields[selectedDateField]?.value) ||
						"MM d yyyy"
				}}
				info={props.info}
				name={selectedDateField || ''}
				readOnly={props.readOnly}
				onChange={handleSettingsChange}
				showAlignment={false}
				showStringFormat={false}
				showDateFormat={!isBillingPeriodField}   // normal formats if not billing period
				showDateRange={false}
				showCurrencyFormat={false}
				onDefaultValueClick={props.onDefaultValueClick}
				isBillingPeriod={isBillingPeriodField}
				isModelMapping={false}
				openMenuAnchor={anchorEl}
				closeMenu={closeDateFormatPopover}
				customFormats={isBillingPeriodField ? 'BillingPeriodFormats' : null} // ✅ inject our custom list
			/>
		</Popover>
	</>);
}


